import { useCallback, useMemo, useState } from "react";
import { useWebSocketContext } from "../context/WebSocketContext";
import { AgentRequest, AgentMessage, WebSocketMessage } from "../types";
import { generateSessionId } from "../utils/websocketUtils";

/**
 * Custom hook for agent-specific WebSocket operations
 */
export const useAgentWebSocket = () => {
    const { state, sendMessage, clearMessages, isReady } = useWebSocketContext();
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentRequest, setCurrentRequest] = useState<AgentRequest | null>(null);
    const messages = state.messages as WebSocketMessage<AgentMessage>[];
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const sessionId = currentSessionId || generateSessionId(currentRequest?.clientId || 0);

    const agentMessages = useMemo(() => {
        return messages.filter((msg: WebSocketMessage<AgentMessage>) => msg.action === "agent-create-widget" && msg.data?.sessionId === sessionId);
    }, [messages, sessionId]);

    const progressMessages = useMemo(() => {
        return agentMessages
            .filter((msg: WebSocketMessage<AgentMessage>) => msg.data?.type === "in-progress" && msg.data?.agentMessage)
            .map((msg) => msg) as unknown as WebSocketMessage<AgentMessage>[];
    }, [agentMessages]);

    const generateSession = useCallback((clientId: number) => {
        return generateSessionId(clientId);
    }, []);

    const clearSessionMessages = useCallback(() => {
        clearMessages((message) => message.action === "agent-create-widget" && message.data?.sessionId === sessionId);
        setIsProcessing(false);
    }, [clearMessages, sessionId]);

    const sendRequest = useCallback(
        (prompt: string, clientId: number, dashboardId: number, pageId: number, tag?: string, userId?: string, publishedToAll?: boolean) => {
            const sessionId = generateSession(clientId);
            setCurrentSessionId(sessionId);

            const request: AgentRequest = {
                prompt,
                clientId,
                dashboardId,
                pageId,
                tag,
                sessionId,
                userId,
                publishedToAll,
            };

            setCurrentRequest(request);
            setIsProcessing(true);

            const message = {
                action: "agent",
                timestamp: Date.now(),
                ...request,
            };

            sendMessage(JSON.stringify(message));
        },
        [sendMessage, generateSession]
    );

    const resetProcessing = useCallback(() => setIsProcessing(false), []);

    return {
        // Connection status
        isReady,
        isProcessing,
        error: state.error,

        // Messages
        messages: agentMessages,
        progressMessages,
        currentRequest,

        // Actions
        sendRequest,
        clearSessionMessages,
        resetProcessing,

        generateSession,
    };
};
